import { yupResolver } from '@hookform/resolvers/yup';
import { GoogleLogin } from '@react-oauth/google';
import { AxiosError } from 'axios';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@cms/components/data/Button';
import { ControlledSystemPasswordInput, ControlledSystemTextInput } from '@cms/components/data/TextInput';
import { AccountBlocked } from '@cms/components/ui/AccountBlocked';
import { SystemBackdrop } from '@cms/components/ui/Backdrop';
import { WrappedLoader } from '@cms/components/ui/Loader';
import { useSnackbar } from '@cms/providers/SnackbarProvider';
import { SignInFormData, signInSchema } from '@cms/utils/forms/auth';
import { Routes } from '@cms/utils/routes';
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks";
import { googleSignInAsyncAction, traditionalSignInAsyncAction } from "@cms/utils/store/slices/user.slice";
import { ERROR_CAUSES, IAuthResponse, ISystemApiError } from "@dl/declarations";
import clsx from 'clsx';
import { useState } from 'react';

const SignInPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { showSnackbar } = useSnackbar();

    const { isLoading } = useAppSelector((state) => state.user);
    const [authCause, setAuthCause] = useState<string | undefined>(undefined);
    const [isTraditionalSignInFormOpen, setIsTraditionalSignInFormOpen] = useState(false);

    const methods = useForm<SignInFormData>({
        defaultValues: {
            email: "",
            password: "",
        },
        mode: "all",
        resolver: yupResolver(signInSchema) as Resolver<SignInFormData>,
    });

    const {
        handleSubmit,
    } = methods;

    const onSubmit = async (data: SignInFormData) => {
        dispatch(traditionalSignInAsyncAction({ email: data.email, password: data.password }))
            .then((data) => {
                if (data.meta.requestStatus === 'fulfilled') {
                    const payload = data.payload as IAuthResponse;
                    onSubmitSuccess(payload);
                }
                else {
                    onSubmitError(data.payload as AxiosError<ISystemApiError>);
                }
            })
            .catch((error) => {
                onSubmitError(error as AxiosError<ISystemApiError>);
            });
    };

    const onSubmitSuccess = (payload: IAuthResponse) => {
        const profile = payload.profile;

        let pathname = Routes.TWO_FA;

        if (!profile.isPasswordSet) {
            pathname = Routes.SET_PASSWORD;
        }

        navigate({ pathname, search });
    }

    const onSubmitError = (error: AxiosError<ISystemApiError>) => {
        const message = error?.response?.data?.message || 'Login failed';
        const cause = error?.response?.data?.cause;
        console.error(error);
        showSnackbar(message, 'error');
        setAuthCause(cause);
    }

    if (authCause === ERROR_CAUSES.AUTH_BLOCKED) {
        return (
            <AccountBlocked />
        )
    }

    return (
        <>
            <SystemBackdrop isOpen={isLoading}>
                <WrappedLoader loaderSize='lg' loaderColor='white' />
            </SystemBackdrop>

            <div className='flex flex-col items-center mb-8'>
                <h2 className='text-3xl font-semibold'>
                    Welcome to the Llamanager
                </h2>

                <p>
                    The Content Management System for DL News and DL Research
                </p>
            </div>


            <GoogleLogin
                size='large'
                shape='rectangular'
                onSuccess={async ({ credential }) => {
                    try {
                        if (credential) {
                            dispatch(googleSignInAsyncAction({ credential }))
                                .then((data) => {
                                    if (data.meta.requestStatus === 'fulfilled') {
                                        onSubmitSuccess(data.payload as IAuthResponse);
                                    }
                                    else {
                                        onSubmitError(data.payload as AxiosError<ISystemApiError>);
                                    }
                                })
                                .catch((error) => {
                                    onSubmitError(error as AxiosError<ISystemApiError>);
                                })
                        }
                        else {
                            showSnackbar('Login failed', 'error');
                        }
                    } catch {
                        showSnackbar('Login failed', 'error');
                    }
                }}
                onError={() => {
                    showSnackbar('Login failed', 'error');
                }}
            />

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-4 hidden"
                >
                    <div
                        className={clsx(
                            "space-y-2 overflow-hidden",
                            isTraditionalSignInFormOpen ? "max-h-none" : "max-h-0"
                        )}
                    >
                        <ControlledSystemTextInput
                            name="email"
                            description="Email"
                            type="email"
                            placeholder="Email"
                            autoComplete="email"
                            required
                        />

                        <ControlledSystemPasswordInput
                            name="password"
                            description="Password"
                            placeholder="Password"
                            autoComplete="current-password"
                            required
                        />

                        <p className='text-sm'>
                            You have 3 login attempts before blocking access.
                        </p>
                    </div>

                    {isTraditionalSignInFormOpen ? (
                        <Button
                            type="submit"
                            className='mx-auto'
                            inputVariant='white_text_blue_bg'
                        >
                            Sign in
                        </Button>
                    ) : (
                        <Button
                            as='button'
                            type="button"
                            className='mx-auto'
                            inputVariant='white_text_blue_bg'
                            onClick={(e) => {
                                e.preventDefault();
                                setIsTraditionalSignInFormOpen(true)
                            }}
                        >
                            Sign in with your email
                        </Button>
                    )}
                </form>
            </FormProvider>
        </>
    )
}

export default SignInPage